import AppCategory from "./AppCategory.ts";
import Model from "./Model";
import {useCookies} from "../composables/useCookies.ts";
import api from "../api.ts";

/**
 * Represents a permission in the application.
 * Each permission is associated with a specific category.
 *
 * @author Jackson Bingham <jackson@onlineimage.com>
 * @date November 2024
 */
class AppPermission extends Model {
    /**
     * The title or name of the permission.
     */
    private _title: string;

    /**
     * The category this permission belongs to.
     */
    private _category: AppCategory;

    /**
     * Creates a new permission by sending a POST request to the server.
     *
     * This method sends the provided permission data to the server, associates it with the specified client ID,
     * and returns the newly created permission along with its success status.
     *
     * @async
     * @param client_id - The client ID of the application the permission will belong to.
     * @param values - The form data to create a new permission, including its title and associated category.
     * @returns A Promise that resolves to a tuple:
     * - The first element is a boolean indicating whether the creation was successful (`true`) or not (`false`).
     * - The second element is the newly created `AppPermission` instance, or `null` if the creation failed.
     * - The third element is an `Error` object if the creation failed, or `undefined` if it succeeded.
     *
     * @example
     * ```ts
     * const [success, newPermission, error] = await AppPermission.create("client123", {
     *   title: "create_account",
     *   category: "101",
     * });
     *
     * if (success) {
     *   console.log(newPermission.title); // "create_account"
     * } else {
     *   console.error(error.message);
     * }
     * ```
     */
    public static async create(client_id: string, values: IPermissionForm): Promise<[boolean, AppPermission, Error?]> {
        const tokenCookie = useCookies().getCookie("access_token");

        try {
            const response = await api.post(
                `/api/oix/roles/permissions`,
                values, // Axios automatically stringifies JSON objects
                {
                    withCredentials: true,
                    headers: {
                        Authorization: `Bearer ${tokenCookie}`,
                    },
                    params: {
                        client_id,
                    }
                }
            );

            const permission: AppPermission = AppPermission.fromJson(response.data.permission);
            return [true, permission];
        } catch (error) {
            console.error(error);
            return [false, null, error];
        }
    }

    /**
     * Retrieves all permissions from the server.
     *
     * @param client_id - The client ID of the application the permissions belong to.
     * @returns A Promise that resolves to a list of `AppPermission` instances.
     * @example
     * ```ts
     * const permissions = await AppPermission.getAll("client123");
     * console.log(permissions.length); // 5
     * ```
     */
    public static async getAll(client_id: string): Promise<AppPermission[]> {
        const tokenCookie = useCookies().getCookie("access_token");

        const response = await api.get(`/api/oix/roles/permissions`, {
            withCredentials: true,
            headers: {
                Authorization: `Bearer ${tokenCookie}`,
            },
            params: {
                client_id, // Axios handles query string construction
            },
        });

        return response.data.permissions.map((permission: any) => AppPermission.fromJson(permission));
    }

    /**
     * Creates a new AppPermission instance from a JSON object.
     * @param json - The JSON object representing the permission.
     * @returns A new `AppPermission` instance.
     * @example
     * const json = { id: "1", title: "create_account", category: { id: "101", title: "Accounts" } };
     * const permission = AppPermission.fromJson(json);
     * console.log(permission.title); // "create_account"
     */
    public static fromJson(json: any): AppPermission {
        return new AppPermission(json.id, json.title, json.category);
    }

    /**
     * Constructs a new AppPermission instance.
     * @param id - The unique identifier for the permission.
     * @param title - The title or name of the permission.
     * @param category - The category object or JSON associated with this permission.
     * @example
     * const permission = new AppPermission("1", "create_account", { id: "101", title: "Accounts" });
     * console.log(permission.permissionString()); // "accounts--create_account"
     */
    constructor(id: string, title: string, category: any) {
        super(id);
        this._title = title;
        this._category = new AppCategory(category.id, category.title);
    }

    /**
     * Gets the title of the permission.
     *
     * @returns The permission title.
     * @example
     * ```ts
     * const permission = new AppPermission("1", "create_account", { id: "101", title: "Accounts" });
     * console.log(permission.title); // "create_account"
     * ```
     */
    public get title(): string {
        return this._title;
    }

    /**
     * Gets the category associated with the permission.
     *
     * @returns A `AppCategory` instance.
     * @example
     * ```ts
     * const permission = new AppPermission("1", "create_account", { id: "101", title: "Accounts" });
     * console.log(permission.category.title); // "Accounts"
     * ```
     */
    public get category(): AppCategory {
        return this._category;
    }

    /**
     * Parses and returns the permission string in the format `category--permission_title`.
     *
     * @returns Parsed permission string in the format `category--permission_title`.
     * @example
     * ```ts
     * const permission = new AppPermission("1", "create_account", { id: "101", title: "Accounts" });
     * console.log(permission.permissionString); // "accounts--create_account"
     * ```
     */
    public get permissionString(): string {
        return `${this.category.title.toLowerCase()}--${this.title.toLowerCase()}`;
    }

    /**
     * Converts the `AppPermission` instance to a plain JSON object.
     *
     * This method overrides the base `toJSON` implementation to include the `title` and `category` fields.
     *
     * @override
     * @returns An object containing the `AppPermission`'s `id`, `title`, and associated category.
     *
     * @example
     * ```ts
     * const permission = new AppPermission("123", "create_account", { id: "201", title: "Accounts" });
     * console.log(permission.toJSON());
     * // { id: "123", title: "create_account", category: { id: "201", title: "Accounts" } }
     * ```
     */
    public override toJSON() {
        return {
            id: this.id,
            title: this._title,
            category: this._category,
        }
    }

    /**
     * Updates the current permission by sending a PATCH request to the server.
     *
     * This method sends the updated permission data along with the client ID to the server,
     * updates the current instance with the response, and returns the updated permission along with its success status.
     *
     * @async
     * @param client_id - The client ID of the application the permission belongs to.
     * @param values - The form data to update the permission, including its updated title and category.
     * @returns A Promise that resolves to a tuple:
     * - The first element is a boolean indicating whether the update was successful (`true`) or not (`false`).
     * - The second element is the updated `AppPermission` instance, or `null` if the update failed.
     * - The third element is an `Error` object if the update failed, or `undefined` if it succeeded.
     *
     * @example
     * ```ts
     * const permission = new AppPermission("1", "create_account", { id: "101", title: "Accounts" });
     * const [success, updatedPermission, error] = await permission.update("client123", {
     *   title: "edit_account",
     *   category: "101",
     * });
     *
     * if (success) {
     *   console.log(updatedPermission.title); // "edit_account"
     * } else {
     *   console.error(error.message);
     * }
     * ```
     */
    public async update(client_id: string, values: IPermissionForm): Promise<[boolean, AppPermission, Error?]> {
        const tokenCookie = useCookies().getCookie("access_token");

        try {
            const response = await api.patch(
                `/api/oix/roles/permissions/${this.id}`,
                values,
                {
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${tokenCookie}`,
                    },
                    params: {
                        client_id,
                    }
                }
            );

            // Update local instance with the response data
            this._title = response.data.permission.category.title;
            this._category = AppCategory.fromJson(response.data.permission.category);
            return [true, this];
        } catch (error) {
            console.error(error);
            return [false, null, error];
        }
    }

    /**
     * Deletes the current Permission by sending a DELETE request to the server.
     *
     * @param client_id - The client ID of the application the role belongs to.
     * @returns A Promise that resolves to a tuple:
     * - The first element is a boolean indicating whether the deletion was successful (`true`) or not (`false`).
     * - The second element is an `Error` object if the deletion failed, or `undefined` if it succeeded.
     *
     * @example
     * ```ts
     * const permission = new AppPermission("1", "create_account", { id: "101", title: "Accounts" });
     * const [success, error] = await permission.destroy("client123");
     *
     * if (success) {
     *   console.log("Permission successfully deleted");
     * } else {
     *   console.error(error.message);
     * }
     * ```
     */
    public async destroy(client_id: string): Promise<[boolean, Error?]> {
        const tokenCookie = useCookies().getCookie("access_token");
        try {
            // Send DELETE request to the server
            await api.delete(`/api/oix/roles/permissions/${this.id}`, {
                headers: {
                    Authorization: `Bearer ${tokenCookie}`,
                },
                params: {
                    client_id,
                },
            });

            return [true];
        } catch (error) {
            return [false, error];
        }
    }
}

export default AppPermission;

/**
 * Interface representing the structure of the form used to create or update a permission.
 */
export interface IPermissionForm {
    /** The title of the permission. */
    title: string;

    /** The ID of the category the permission belongs to. */
    category: string;
}